import { Box, Button, Center, Flex, Text, VStack } from '@chakra-ui/react';
import { Fragment, useEffect, useRef, useState } from 'react';

import { ContactMessageEntry } from '../../../../../../services/marketplace/generated/kubb/models/ContactMessageEntry';
import { formatDayDivider, localDayKey } from '../../../../../../utils/dates';
import { formatMoney } from '../../../../../../utils/helpers';
import { OfferActionKind, OfferCardView } from '../offers/utils';
import LiveOfferCard from './LiveOfferCard';
import MessageBubble from './MessageBubble';
import MoneyCard from './MoneyCard';
import {
  ConversationTarget,
  TimelineItem,
  WonConversation,
  getTimelineItems,
  isNotedDealerOffer,
  targetHistory,
  targetLabel,
  timelineTimestamp,
} from './utils';

type MessageListProps = {
  target: ConversationTarget;
  thread: ContactMessageEntry[] | undefined;
  onAction?: (kind: OfferActionKind, card: OfferCardView) => void;
  /** Increments when a driver action lands; re-anchors the list to the newest event. */
  threadReanchorSignal?: number;
};

const DayDivider = ({ label }: { label: string }) => (
  <Flex align="center" gap="10px" py="2px">
    <Box flex="1" h="1px" bg="grayMediumBackground" />
    <Text fontSize="10px" fontWeight="700" letterSpacing="0.1em" color="grayLight">
      {label}
    </Text>
    <Box flex="1" h="1px" bg="grayMediumBackground" />
  </Flex>
);

const AcceptedLine = ({ won }: { won: WonConversation }) => (
  <Flex align="center" gap="10px" py="2px">
    <Box flex="1" h="1px" bg="leaseEndGreen" opacity={0.4} />
    <Text fontSize="11px" fontWeight="700" color="greenDark" textAlign="center">
      Offer accepted{won.amount != null && ` · ${formatMoney(won.amount)}`} · {won.dealershipName}
    </Text>
    <Box flex="1" h="1px" bg="leaseEndGreen" opacity={0.4} />
  </Flex>
);

const NewMessagePrompt = ({ onClick }: { onClick: () => void }) => (
  <Center position="sticky" bottom="4px" mt="8px">
    <Button
      size="xs"
      borderRadius="full"
      bg="leaseEndBlue"
      color="white"
      boxShadow="md"
      px="14px"
      _hover={{ bg: 'oceanBoatBlue' }}
      onClick={onClick}
    >
      New message
    </Button>
  </Center>
);

const MessageList = ({ target, thread, onAction, threadReanchorSignal = 0 }: MessageListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Tracks whether the driver is at the bottom so a new event only auto-scrolls when they
  // haven't scrolled up to read older ones. Starts true so the list opens at the newest.
  const atBottomRef = useRef(true);

  const won = target.kind === 'won' ? target : undefined;
  const dealerLabel = targetLabel(target);
  const history = targetHistory(target);

  const items = getTimelineItems(thread, history);
  const itemCount = items.length;

  // The active thread's current row (the pending dealer offer, or the driver's pending counter)
  // renders as the LiveOfferCard; every other money event renders as a MoneyCard.
  const currentRowId =
    target.kind === 'active' ? target.card.driverCounter?.id ?? target.card.offer.id : null;

  const [showNewMessagePrompt, setShowNewMessagePrompt] = useState(false);
  const previousItemCountRef = useRef(itemCount);

  useEffect(() => {
    atBottomRef.current = true;
    previousItemCountRef.current = itemCount;
    setShowNewMessagePrompt(false);
  }, [dealerLabel]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el != null && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [dealerLabel, itemCount]);

  useEffect(() => {
    const previousItemCount = previousItemCountRef.current;
    previousItemCountRef.current = itemCount;
    if (itemCount <= previousItemCount || atBottomRef.current) return;
    const dealerItemArrived = items
      .slice(previousItemCount)
      .some((item) =>
        item.kind === 'message'
          ? item.message.senderRole !== 'driver'
          : isNotedDealerOffer(item.offer),
      );
    if (dealerItemArrived) setShowNewMessagePrompt(true);
  }, [itemCount, items]);

  const scrollToBottom = () => {
    atBottomRef.current = true;
    setShowNewMessagePrompt(false);
    const el = scrollRef.current;
    if (el != null) el.scrollTop = el.scrollHeight;
  };

  // After a driver action, force-scroll to the newest event even if they'd scrolled up.
  useEffect(() => {
    if (threadReanchorSignal === 0) return;
    scrollToBottom();
  }, [threadReanchorSignal]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el == null) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (atBottomRef.current) setShowNewMessagePrompt(false);
  };

  // The accepted divider sits before the first timeline event after acceptance, or at the very
  // end when every event predates it.
  const acceptedAtTimestamp = won?.acceptedAt == null ? null : Date.parse(won.acceptedAt);
  const firstAfterAccepted =
    acceptedAtTimestamp == null
      ? -1
      : items.findIndex((item) => Date.parse(timelineTimestamp(item)) > acceptedAtTimestamp);
  const acceptedLineIndex = firstAfterAccepted === -1 ? itemCount : firstAfterAccepted;

  // A won thread with no events still renders its accepted line, so only the non-won empty
  // thread shows the say-hello prompt.
  if (itemCount === 0 && won == null) {
    return (
      <Center flex="1" px="24px" textAlign="center">
        <Text fontSize="14px" color="grayMedium">
          No messages yet. Say hello.
        </Text>
      </Center>
    );
  }

  const renderItem = (item: TimelineItem) => {
    if (item.kind === 'message') return <MessageBubble message={item.message} won={won != null} />;
    if (target.kind === 'active' && onAction != null && item.offer.id === currentRowId)
      return <LiveOfferCard card={target.card} onAction={onAction} />;
    return <MoneyCard offer={item.offer} />;
  };

  return (
    <Box ref={scrollRef} onScroll={handleScroll} flex="1" overflowY="auto" px="16px" py="12px">
      <VStack align="stretch" spacing="8px">
        {items.map((item, index) => {
          const showDivider =
            index === 0 ||
            localDayKey(timelineTimestamp(item)) !==
              localDayKey(timelineTimestamp(items[index - 1]));
          const itemKey =
            item.kind === 'message' ? `message-${item.message.id}` : `money-${item.offer.id}`;
          return (
            <Fragment key={itemKey}>
              {index === acceptedLineIndex && won != null && <AcceptedLine won={won} />}
              {showDivider && <DayDivider label={formatDayDivider(timelineTimestamp(item))} />}
              {renderItem(item)}
            </Fragment>
          );
        })}
        {acceptedLineIndex === itemCount && won != null && <AcceptedLine won={won} />}
      </VStack>
      {showNewMessagePrompt && <NewMessagePrompt onClick={scrollToBottom} />}
    </Box>
  );
};

export default MessageList;
